import { useCallback } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { UsersIcon } from "@lucide/icons";

import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Card, CardContent, CardDescription } from "simplydo/react/ui/card";
import { H2, H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Message } from "simplydo/react/ui/message";

import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";

import type { InviteCardProps } from "./types";

const RequestJoinIdeaInviteCard = ({
  invitation,
  acceptInvite,
  rejectInvite,
  removeInvite,
  cardView = true,
}: InviteCardProps) => {
  const { inviterUser, idea, createdByCurrentUser } = invitation;
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();

  const accept = useCallback(() => {
    util
      .confirm(
        `Allow ${t("generic.idea")} join`,
        `Are you sure you want to allow ${inviterUser?.profile?.fullName} to join "${idea?.name}"?`,
      )
      .then(() => {
        acceptInvite(
          invitation,
          () => {
            toast.success("Accepted request");
          },
          () => toast.error("Error while accepting request"),
        );
      })
      .catch(() => {});
  }, [acceptInvite, invitation, idea?.name, inviterUser?.profile?.fullName, t]);

  const reject = useCallback(() => {
    util
      .confirm(
        `Reject ${t("generic.idea")} join`,
        `Are you sure you want to reject ${inviterUser?.profile?.fullName} from joining "${idea?.name || `Deleted ${t("generic.idea")}`}"?`,
      )
      .then(() => {
        rejectInvite(
          invitation,
          () => {
            toast("Request rejected");
          },
          () => toast.error("Error rejecting request"),
        );
      })
      .catch(() => {});
  }, [rejectInvite, invitation, idea?.name, inviterUser?.profile?.fullName, t]);

  const remove = useCallback(() => {
    util
      .confirm(
        "Remove request",
        `Are you sure you want to remove your request to join "${idea?.name || `Deleted ${t("generic.idea")}`}"?`,
      )
      .then(() => {
        removeInvite(
          invitation,
          () => {
            toast("Invitation removed");
          },
          () => toast.error("Error removing invite"),
        );
      })
      .catch(() => {});
  }, [removeInvite, invitation, idea?.name, t]);

  const canAccept = invitation.canAccept && !!idea;

  return (
    <div
      style={
        cardView
          ? {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }
          : {}
      }
    >
      <div>
        {cardView ? (
          <H3 style={{ marginBottom: "0.3em" }}>
            <Link to={idea ? `/ideas/${idea._id}` : ""}>
              {idea?.name || `Deleted ${t("generic.idea")}`}
            </Link>
          </H3>
        ) : (
          <H2 style={{ textAlign: "center" }}>
            <Icon icon={UsersIcon} />
            Request to join {idea?.name || `Deleted ${t("generic.idea")}`}
          </H2>
        )}
        <CardDescription>
          {createdByCurrentUser ? (
            `You have requested to join this ${t("generic.idea")}`
          ) : (
            <UserChip
              compact="very"
              user={inviterUser}
              postText={`has requested to join this ${t("generic.idea")}`}
            />
          )}
        </CardDescription>
      </div>
      <CardContent>
        <div
          style={
            cardView
              ? { display: "flex", justifyContent: "flex-end" }
              : { marginTop: 20, display: "flex", justifyContent: "flex-end" }
          }
        >
          {invitation.acceptedAt && idea && (
            <ButtonLink
              color="green"
              variant="outline"
              to={`/ideas/${idea?._id}`}
            >
              Go to {t("generic.idea")}
            </ButtonLink>
          )}
          {!invitation.acceptedAt && (
            <>
              {!idea && (
                <Message color="red" style={{ width: "100%" }}>
                  This request to join is no longer valid because the{" "}
                  {t("generic.idea")} has been deleted.
                </Message>
              )}
              {user && invitation.canRemove ? (
                <Button
                  color={createdByCurrentUser ? undefined : "red"}
                  onClick={createdByCurrentUser ? remove : reject}
                  variant="outline"
                >
                  {createdByCurrentUser ? "Remove" : "Reject"}
                </Button>
              ) : null}
              {user && canAccept ? (
                <Button color="green" onClick={accept} variant="outline">
                  Accept
                </Button>
              ) : null}
            </>
          )}
        </div>
      </CardContent>
    </div>
  );
};

export default RequestJoinIdeaInviteCard;
